import * as React from 'react'
import type { DateRange } from 'react-day-picker'
import {
 addMonths,
 endOfMonth,
 endOfWeek,
 startOfMonth,
 startOfWeek,
 subDays,
 subMonths,
 subWeeks,
} from 'date-fns'
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { dateTimeLocalMinuteInAppTimezone, formatDateLabelStable, todayLocalDateString } from 'helpers/dateTime'
import { normalizeAppLanguage } from 'helpers/documentLocale'
import { businessWeekStartsOn } from 'helpers/businessWeek'
import { semanticDatePresetRange } from 'helpers/semanticDatePresets'
import { getUserPreference, removeUserPreference, setUserPreference, type SemanticDatePreset } from 'services/userPreferences'
import { cn } from '../lib/utils'
import { Button } from './ui/button'
import { Calendar } from './ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from './ui/dialog'
import { calendarDateToYmd } from './date-picker'

export type DateRangeValue = { from: string | null; to: string | null }

export type DateRangePickerProps = {
 id?: string
 value: DateRangeValue
 onChange: (value: DateRangeValue) => void
 placeholder?: string
 disabled?: boolean
 clearable?: boolean
 includeAllTimePreset?: boolean
 rememberPreset?: boolean
 includeTime?: boolean
 className?: string
 'aria-label'?: string
}

type PresetDef = {
 id: string
 label: string
 getRange: () => { from: Date; to: Date }
}

function datePart(value: string): string {
 return value.slice(0, 10)
}

function timePart(value: string | null, fallback: string): string {
 const match = value ? /^\d{4}-\d{2}-\d{2}[T ](\d{2}:\d{2})/.exec(value) : null
 return match?.[1] ?? fallback
}

function combineDateTime(date: string | null, time: string): string | null {
 return date ? `${datePart(date)}T${time}` : null
}

function rollingLast24Hours(): DateRangeValue {
 const end = new Date()
 end.setSeconds(0, 0)
 const start = new Date(end.getTime() - 24 * 60 * 60 * 1000)
 return {
  from: dateTimeLocalMinuteInAppTimezone(start),
  to: dateTimeLocalMinuteInAppTimezone(end),
 }
}

function parseYmdLocal(ymd: string): Date {
 const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart(ymd.trim()))
 if (!m) return new Date(Number.NaN)
 return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

function normalizeDateRangeValue(value: DateRangeValue, sort = true): DateRangeValue {
 if (sort && value.from && value.to && value.from > value.to) {
  return { from: value.to, to: value.from }
 }

 return value
}

function buildPresets(t: (key: string) => string): PresetDef[] {
 const today = () => parseYmdLocal(todayLocalDateString())
 const weekOpts = { weekStartsOn: businessWeekStartsOn() }

 return [
  {
   id: 'today',
   label: t('ui.dateRange.presets.today'),
   getRange: () => {
    const d = today()
    return { from: d, to: d }
   },
  },
  {
   id: 'yesterday',
   label: t('ui.dateRange.presets.yesterday'),
   getRange: () => {
    const d = subDays(today(), 1)
    return { from: d, to: d }
   },
  },
  {
   id: 'last24Hours',
   label: t('ui.filters.presets.24h'),
   getRange: () => ({ from: subDays(today(), 1), to: today() }),
  },
  {
   id: 'last7Days',
   label: t('ui.dateRange.presets.last7Days'),
   getRange: () => ({ from: subDays(today(), 6), to: today() }),
  },
  {
   id: 'last30Days',
   label: t('ui.dateRange.presets.last30Days'),
   getRange: () => ({ from: subDays(today(), 29), to: today() }),
  },
  {
   id: 'thisWeek',
   label: t('ui.dateRange.presets.thisWeek'),
   getRange: () => ({ from: startOfWeek(today(), weekOpts), to: today() }),
  },
  {
   id: 'lastWeek',
   label: t('ui.dateRange.presets.lastWeek'),
   getRange: () => {
    const prevWeek = subWeeks(today(), 1)
    return {
     from: startOfWeek(prevWeek, weekOpts),
     to: endOfWeek(prevWeek, weekOpts),
    }
   },
  },
  {
   id: 'thisMonth',
   label: t('ui.dateRange.presets.thisMonth'),
   getRange: () => ({ from: startOfMonth(today()), to: today() }),
  },
  {
   id: 'lastMonth',
   label: t('ui.dateRange.presets.lastMonth'),
   getRange: () => {
    const prev = subMonths(today(), 1)
    return { from: startOfMonth(prev), to: endOfMonth(prev) }
   },
  },
  {
   id: 'last90Days',
   label: t('ui.dateRange.presets.last90Days'),
   getRange: () => ({ from: subDays(today(), 89), to: today() }),
  },
 ]
}

function toDateRange(value: DateRangeValue): DateRange | undefined {
 const normalizedValue = normalizeDateRangeValue(value, false)
 if (!normalizedValue.from && !normalizedValue.to) return undefined
 const from = normalizedValue.from ? parseYmdLocal(normalizedValue.from) : undefined
 const to = normalizedValue.to ? parseYmdLocal(normalizedValue.to) : undefined
 if (from && to && from > to) return { from: to, to: from }
 return {
  from,
  to,
 }
}

function fromDateRange(range: DateRange | undefined): DateRangeValue {
 if (!range?.from && !range?.to) return { from: null, to: null }
 return normalizeDateRangeValue({
  from: range?.from ? calendarDateToYmd(range.from) : null,
  to: range?.to ? calendarDateToYmd(range.to) : null,
 })
}

function formatRangeAriaLabel(
 value: DateRangeValue,
 t: (key: string, opts?: { from?: string; to?: string }) => string,
 includeTime = false,
): string {
 const normalizedValue = normalizeDateRangeValue(value, !includeTime)
 const label = (entry: string | null) => {
  if (!entry) return ''
  const day = formatDateLabelStable(datePart(entry))
  return includeTime ? `${day} ${timePart(entry, '00:00')}` : day
 }
 if (normalizedValue.from && normalizedValue.to) {
  return t('ui.dateRange.displayFromTo', {
   from: label(normalizedValue.from),
   to: label(normalizedValue.to),
  })
 }
 if (normalizedValue.from) return label(normalizedValue.from)
 if (normalizedValue.to) return label(normalizedValue.to)
 return ''
}

const dateLabelClass =
 'inline-block font-semibold text-foreground tabular-nums [unicode-bidi:isolate]'

function RangeLabelDisplay({
 value,
 rtl,
 t,
 includeTime = false,
}: {
 value: DateRangeValue
 rtl: boolean
 t: (key: string, opts?: { from?: string; to?: string }) => string
 includeTime?: boolean
}) {
 const normalizedValue = normalizeDateRangeValue(value, !includeTime)
 const label = (entry: string | null) => {
  if (!entry) return ''
  const day = formatDateLabelStable(datePart(entry))
  return includeTime ? `${day} ${timePart(entry, '00:00')}` : day
 }
 if (normalizedValue.from && normalizedValue.to) {
  const fromLabel = label(normalizedValue.from)
  const toLabel = label(normalizedValue.to)
  if (rtl) {
   return (
    <>
     <span>{t('ui.dateRange.displayFromPrefix')}{' '}</span>
     <span dir="ltr" className={dateLabelClass}>
      {fromLabel}
     </span>
     <span>{' '}{t('ui.dateRange.displayToPrefix')}{' '}</span>
     <span dir="ltr" className={dateLabelClass}>
      {toLabel}
     </span>
    </>
   )
  }
  return (
   <span dir="ltr" className={cn(dateLabelClass, 'font-semibold')}>
    {fromLabel}
    <span className="mx-1 font-normal text-muted-foreground">—</span>
    {toLabel}
   </span>
  )
 }

 const single = normalizedValue.from || normalizedValue.to
 if (single) {
  return (
   <span dir="ltr" className={dateLabelClass}>
    {label(single)}
   </span>
  )
 }

 return null
}

function rangesEqual(a: DateRangeValue, b: DateRangeValue): boolean {
 return a.from === b.from && a.to === b.to
}

const presetChipClass =
 'flex h-9 min-h-9 w-full items-center rounded-lg border px-3 text-xs font-medium transition-colors duration-150 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:ring-offset-1'

function isAllTimeValue(value: DateRangeValue, includeAllTimePreset?: boolean): boolean {
 return Boolean(includeAllTimePreset && !value.from && !value.to)
}

export function DateRangePicker({
 id,
 value,
 onChange,
 placeholder,
 disabled,
 className,
 clearable = true,
 includeAllTimePreset = true,
 rememberPreset = false,
 includeTime = false,
 'aria-label': ariaLabel,
}: DateRangePickerProps) {
 const { t, i18n } = useTranslation()
 const [open, setOpen] = React.useState(false)
 const { from: valueFrom, to: valueTo } = value
 const normalizedValue = normalizeDateRangeValue({ from: valueFrom, to: valueTo }, !includeTime)
 const [draftRange, setDraftRange] = React.useState<DateRangeValue>(normalizedValue)
 const [draftFromTime, setDraftFromTime] = React.useState(() => timePart(valueFrom, '00:00'))
 const [draftToTime, setDraftToTime] = React.useState(() => timePart(valueTo, '23:59'))
 const [month, setMonth] = React.useState<Date>(() => {
  const range = toDateRange(normalizedValue)
  return range?.from ?? range?.to ?? new Date()
 })
 const [twoMonths, setTwoMonths] = React.useState(false)
 const [mobileDialog, setMobileDialog] = React.useState(false)
 const lang = normalizeAppLanguage(i18n.language)
 const rtl = lang === 'ar'
 const resolvedPlaceholder = placeholder ?? t('ui.dateRange.placeholder')
 const allTimeLabel = t('ui.dateRange.presets.all', { defaultValue: t('reports.common.all') })
 const displayValue = open ? draftRange : normalizedValue
 const isAllTime = isAllTimeValue(normalizedValue, includeAllTimePreset)
 const isDisplayAllTime = isAllTimeValue(displayValue, includeAllTimePreset)
 const hasValue = Boolean(normalizedValue.from || normalizedValue.to) || isAllTime
 const hasDisplayValue = Boolean(displayValue.from || displayValue.to) || isDisplayAllTime
 const draftSelected = toDateRange(draftRange)
 const startDateTime = combineDateTime(draftRange.from, draftFromTime)
 const endDateTime = combineDateTime(draftRange.to, draftToTime)
 const timesAreValid = /^([01]\d|2[0-3]):[0-5]\d$/.test(draftFromTime) && /^([01]\d|2[0-3]):[0-5]\d$/.test(draftToTime)
 const rangeIsOrdered = !includeTime || !startDateTime || !endDateTime || endDateTime >= startDateTime
 const canApply = Boolean(draftRange.from && draftRange.to && rangeIsOrdered && timesAreValid)
 const monthCount = twoMonths ? 2 : 1

 const presets = React.useMemo(() => buildPresets(t), [t])
 const restoredPresetRef = React.useRef(false)

 React.useEffect(() => {
  if (typeof window === 'undefined') return
  if (typeof window.matchMedia !== 'function') {
   setTwoMonths(false)
   return
  }
  const desktopMonths = window.matchMedia('(min-width: 1024px)')
  const mobileLayout = window.matchMedia('(max-width: 480px)')
  const sync = () => {
   setTwoMonths(desktopMonths.matches)
   setMobileDialog(mobileLayout.matches)
  }
  sync()
  if (typeof desktopMonths.addEventListener === 'function') {
   desktopMonths.addEventListener('change', sync)
   mobileLayout.addEventListener('change', sync)
   return () => {
    desktopMonths.removeEventListener('change', sync)
    mobileLayout.removeEventListener('change', sync)
   }
  }
  desktopMonths.addListener(sync)
  mobileLayout.addListener(sync)
  return () => {
   desktopMonths.removeListener(sync)
   mobileLayout.removeListener(sync)
  }
 }, [])

 React.useEffect(() => {
  if (!open) {
   setDraftRange(normalizeDateRangeValue({ from: valueFrom, to: valueTo }, !includeTime))
   setDraftFromTime(timePart(valueFrom, '00:00'))
   setDraftToTime(timePart(valueTo, '23:59'))
  }
 }, [open, valueFrom, valueTo, includeTime])

 React.useEffect(() => {
  if (!rememberPreset || restoredPresetRef.current) return
  restoredPresetRef.current = true
  // A non-empty caller value is explicit and must take priority over memory.
  if (normalizedValue.from || normalizedValue.to) return
  const savedPreset = getUserPreference('reports.datePreset')
  if (!savedPreset || savedPreset === 'all') return
  onChange(
   includeTime && savedPreset === 'last24Hours'
    ? rollingLast24Hours()
    : semanticDatePresetRange(savedPreset, new Date(), businessWeekStartsOn()),
  )
 }, [rememberPreset, normalizedValue.from, normalizedValue.to, onChange, includeTime])

 const handleOpenChange = (next: boolean) => {
  if (next) {
   setDraftRange(normalizedValue)
   setDraftFromTime(timePart(valueFrom, '00:00'))
   setDraftToTime(timePart(valueTo, '23:59'))
   const range = toDateRange(normalizedValue)
   setMonth(range?.from ?? range?.to ?? new Date())
  }
  setOpen(next)
 }

 const applyDraft = (next: DateRangeValue, preset?: SemanticDatePreset) => {
  if (rememberPreset) {
   if (preset) setUserPreference('reports.datePreset', preset)
   else removeUserPreference('reports.datePreset')
  }
  const valueToApply = includeTime
   ? {
      from: combineDateTime(next.from, preset ? timePart(next.from, draftFromTime) : draftFromTime),
      to: combineDateTime(next.to, preset ? timePart(next.to, draftToTime) : draftToTime),
     }
   : normalizeDateRangeValue(next)
  onChange(valueToApply)
  setOpen(false)
 }

 const applyPreset = (presetId: SemanticDatePreset, from: Date, to: Date) => {
  let next: DateRangeValue = {
   from: calendarDateToYmd(from),
   to: calendarDateToYmd(to),
  }
  if (includeTime) {
   let startTime = '00:00'
   let endTime = '23:59'
   if (presetId === 'last24Hours') {
    next = rollingLast24Hours()
    startTime = timePart(next.from, '00:00')
    endTime = timePart(next.to, '23:59')
   }
   setDraftFromTime(startTime)
   setDraftToTime(endTime)
   const fullDayRange = {
    from: next.from?.includes('T') ? next.from : combineDateTime(next.from, startTime),
    to: next.to?.includes('T') ? next.to : combineDateTime(next.to, endTime),
   }
   setDraftRange(fullDayRange)
   applyDraft(fullDayRange, presetId)
   return
  }
  setDraftRange(next)
  applyDraft(next, presetId)
 }

 const applyAllTime = () => {
  const next = { from: null, to: null }
  setDraftRange(next)
  applyDraft(next, 'all')
 }

 const activePresetId = React.useMemo(() => {
  if (includeAllTimePreset && !draftRange.from && !draftRange.to) return 'all'
  for (const preset of presets) {
   const { from, to } = preset.getRange()
   const candidate = {
    from: calendarDateToYmd(from),
    to: calendarDateToYmd(to),
   }
   if (
    rangesEqual(
     { from: draftRange.from ? datePart(draftRange.from) : null, to: draftRange.to ? datePart(draftRange.to) : null },
     candidate,
    ) && (!includeTime || (draftFromTime === '00:00' && draftToTime === '23:59'))
   ) return preset.id
  }
  return null
 }, [draftRange, draftFromTime, draftToTime, includeAllTimePreset, includeTime, presets])

 const hintMessage = isAllTimeValue(draftRange, includeAllTimePreset)
  ? allTimeLabel
  : !draftRange.from
  ? t('ui.dateRange.hint.selectStart')
  : !draftRange.to
   ? t('ui.dateRange.hint.selectEnd')
   : t('ui.dateRange.hint.ready')

 const goPrevMonth = () => setMonth((current) => subMonths(current, monthCount))
 const goNextMonth = () => setMonth((current) => addMonths(current, monthCount))

 const ariaRangeLabel = isDisplayAllTime
  ? allTimeLabel
  : hasDisplayValue
   ? formatRangeAriaLabel(displayValue, t, includeTime)
   : undefined

 const trigger = (
    <button
     id={id}
     type="button"
     disabled={disabled}
     dir={rtl ? 'rtl' : 'ltr'}
     aria-label={ariaLabel ?? ariaRangeLabel ?? resolvedPlaceholder}
     aria-expanded={open}
     className={cn(
      'flex h-10 min-h-10 w-full items-center rounded-xl border px-3 py-2 text-sm shadow-sm transition-all',
      'text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0',
      hasValue
       ? 'border-[hsl(var(--primary-selection-border))] bg-[hsl(var(--primary-soft-surface))]/40 hover:bg-[hsl(var(--primary-soft-surface))]/55'
       : 'border-border-subtle bg-background hover:bg-muted/40',
      open && 'ring-2 ring-[hsl(var(--primary))]/25',
      'focus-visible:ring-[hsl(var(--primary))]/30',
      disabled && 'cursor-not-allowed opacity-60',
      className
     )}
    >
     <CalendarIcon
      className={cn(
       'pointer-events-none me-2 h-4 w-4 shrink-0',
       hasValue || open ? 'text-[hsl(var(--primary))]' : 'text-muted-foreground'
      )}
      aria-hidden
     />
     <span className={cn('min-w-0 flex-1 truncate', !hasDisplayValue && 'font-normal text-muted-foreground')}>
      {isDisplayAllTime ? (
       <span className="font-semibold text-foreground">{allTimeLabel}</span>
      ) : hasDisplayValue ? (
       <RangeLabelDisplay value={displayValue} rtl={rtl} t={t} includeTime={includeTime} />
      ) : (
       resolvedPlaceholder
      )}
     </span>
     {clearable && hasDisplayValue && !isDisplayAllTime && !disabled && !open ? (
      <span
       role="button"
       tabIndex={0}
       className="ms-2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
       aria-label={t('ui.dateRange.presets.clear')}
       onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        if (rememberPreset) removeUserPreference('reports.datePreset')
        onChange({ from: null, to: null })
       }}
       onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
         e.preventDefault()
         e.stopPropagation()
         if (rememberPreset) removeUserPreference('reports.datePreset')
         onChange({ from: null, to: null })
        }
       }}
      >
       <X className="h-4 w-4" />
      </span>
     ) : null}
    </button>
 )

 const pickerPanel = (
  <div className={cn('flex min-w-0 flex-col sm:flex-row', mobileDialog && 'w-full')}>
     <div className="date-range-picker-presets border-b border-border-subtle bg-primary-soft/30 p-3 sm:w-48 md:w-40 lg:w-48 sm:border-b-0 sm:border-e sm:border-primary-soft-border/40">
      <p className="mb-2 text-xs font-bold text-foreground sm:text-sm">
       {t('ui.dateRange.presets.title')}
      </p>
      <div className="flex max-h-[min(20rem,45vh)] flex-col gap-1.5 overflow-y-auto overscroll-contain max-[480px]:max-h-[min(10rem,24dvh)]">
       {presets.map((preset) => {
        const { from, to } = preset.getRange()
        const isActive = activePresetId === preset.id
        return (
         <button
          key={preset.id}
          type="button"
          className={cn(
           presetChipClass,
           isActive
            ? 'border-primary bg-primary font-semibold text-primary-foreground shadow-sm'
            : 'border-primary-soft-border/70 bg-card text-foreground shadow-sm hover:border-primary/35 hover:bg-primary-soft/80 active:scale-[0.99]'
          )}
          onClick={() => applyPreset(preset.id as SemanticDatePreset, from, to)}
         >
          {preset.label}
         </button>
        )
       })}
       {includeAllTimePreset ? (
        <button
         type="button"
         className={cn(
          presetChipClass,
          activePresetId === 'all'
           ? 'border-primary bg-primary font-semibold text-primary-foreground shadow-sm'
           : 'border-primary-soft-border/70 bg-card text-foreground shadow-sm hover:border-primary/35 hover:bg-primary-soft/80 active:scale-[0.99]'
         )}
         onClick={applyAllTime}
        >
         {allTimeLabel}
        </button>
       ) : null}
      </div>
     </div>

     <div className="flex min-w-0 flex-1 flex-col">
      <div className="date-range-picker-toolbar flex items-center justify-between gap-2 border-b border-border-subtle bg-muted/30 px-3 py-2">
       <p className="date-range-picker-hint flex min-w-0 flex-1 items-center gap-2 text-sm font-medium text-foreground/90">
        <span
         className="h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--primary))]"
         aria-hidden
        />
        <span className="truncate">{hintMessage}</span>
       </p>
       <div className="flex shrink-0 items-center gap-1">
        <Button
         type="button"
         variant="outline"
         size="icon-sm"
         className="h-8 w-8 border-[hsl(var(--primary-selection-border))]/50 hover:bg-[hsl(var(--primary-soft-surface))]"
         aria-label={t('ui.dateRange.navPrevious')}
         onClick={goPrevMonth}
        >
         {rtl ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
        <Button
         type="button"
         variant="outline"
         size="icon-sm"
         className="h-8 w-8 border-[hsl(var(--primary-selection-border))]/50 hover:bg-[hsl(var(--primary-soft-surface))]"
         aria-label={t('ui.dateRange.navNext')}
         onClick={goNextMonth}
        >
         {rtl ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>
       </div>
      </div>
      <div className="bg-card p-3">
       <Calendar
        density="compact"
        mode="range"
        month={month}
        onMonthChange={setMonth}
        numberOfMonths={monthCount}
        hideNavigation
        pagedNavigation={monthCount > 1}
        selected={draftSelected}
        onSelect={(range) => {
         const dates = fromDateRange(range)
         const next = includeTime
          ? {
             from: combineDateTime(dates.from, draftFromTime),
             to: combineDateTime(dates.to, draftToTime),
            }
          : normalizeDateRangeValue(dates)
         setDraftRange(next)
         if (next.from) setMonth(parseYmdLocal(datePart(next.from)))
        }}
       />
      </div>
      {includeTime ? (
       <div className="grid gap-3 border-t border-border-subtle bg-card px-4 py-3 lg:grid-cols-2">
        <label className="flex min-w-0 flex-col gap-1.5">
         <span className="text-xs font-medium text-muted-foreground">{t('ui.dateTime.start')}</span>
         <div className="flex min-h-10 items-center gap-2 rounded-xl border border-border-subtle bg-background px-3 shadow-sm">
          <span dir="ltr" className="min-w-0 flex-1 truncate text-sm tabular-nums [unicode-bidi:isolate]">
           {draftRange.from ? formatDateLabelStable(datePart(draftRange.from)) : t('ui.dateTime.chooseDate')}
          </span>
          <input
           type="time"
           step={60}
           lang="en-GB"
           dir="ltr"
           value={draftFromTime}
           onChange={(event) => setDraftFromTime(event.target.value)}
           disabled={!draftRange.from}
           aria-label={t('ui.dateTime.start')}
           className="h-8 w-28 shrink-0 rounded-lg border border-border-subtle bg-background px-2 text-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
         </div>
        </label>
        <label className="flex min-w-0 flex-col gap-1.5">
         <span className="text-xs font-medium text-muted-foreground">{t('ui.dateTime.end')}</span>
         <div className="flex min-h-10 items-center gap-2 rounded-xl border border-border-subtle bg-background px-3 shadow-sm">
          <span dir="ltr" className="min-w-0 flex-1 truncate text-sm tabular-nums [unicode-bidi:isolate]">
           {draftRange.to ? formatDateLabelStable(datePart(draftRange.to)) : t('ui.dateTime.chooseDate')}
          </span>
          <input
           type="time"
           step={60}
           lang="en-GB"
           dir="ltr"
           value={draftToTime}
           onChange={(event) => setDraftToTime(event.target.value)}
           disabled={!draftRange.to}
           aria-label={t('ui.dateTime.end')}
           className="h-8 w-28 shrink-0 rounded-lg border border-border-subtle bg-background px-2 text-sm tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
         </div>
        </label>
        {!rangeIsOrdered ? (
         <p role="alert" className="text-xs text-destructive lg:col-span-2">{t('ui.dateTime.invalidRange')}</p>
        ) : null}
       </div>
      ) : null}
      <div
       className={cn(
        'date-range-picker-footer flex items-center gap-2 border-t border-border-subtle bg-muted/20 px-3 py-2',
        mobileDialog && 'sticky bottom-0 z-20',
        rtl ? 'flex-row-reverse justify-start' : 'justify-end'
       )}
      >
       <Button type="button" variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setOpen(false)}>
        {t('ui.cancel')}
       </Button>
       <Button
        type="button"
        size="sm"
        disabled={!canApply}
        className="min-w-[5.5rem] bg-primary font-semibold text-primary-foreground hover:bg-primary-hover"
        onClick={() => applyDraft(draftRange)}
       >
        {t('ui.dateRange.apply')}
       </Button>
      </div>
     </div>
  </div>
 )

 if (mobileDialog) {
  return (
   <Dialog open={open} onOpenChange={handleOpenChange}>
    <DialogTrigger asChild>{trigger}</DialogTrigger>
    <DialogContent
     className="w-[calc(100vw-1rem)] max-w-none max-h-[calc(100dvh-1rem)] gap-0 overflow-hidden rounded-2xl border-border-subtle bg-popover p-0"
     hideClose
    >
     <DialogTitle className="sr-only">{resolvedPlaceholder}</DialogTitle>
     <div className="min-h-0 max-h-[calc(100dvh-1rem)] w-full overflow-y-auto overflow-x-hidden overscroll-contain">
      {pickerPanel}
     </div>
    </DialogContent>
   </Dialog>
  )
 }

 return (
  <Popover open={open} onOpenChange={handleOpenChange}>
   <PopoverTrigger asChild>{trigger}</PopoverTrigger>
   <PopoverContent
    side="bottom"
    align={rtl ? 'end' : 'start'}
    collisionPadding={12}
    className="date-range-picker-popover z-[110000] w-max min-w-0 max-w-[min(58rem,calc(100vw-1.5rem))] max-h-[calc(100dvh-1.5rem)] overflow-hidden rounded-2xl border-border p-0 shadow-premium-lg"
    dir={rtl ? 'rtl' : 'ltr'}
   >
    {pickerPanel}
   </PopoverContent>
  </Popover>
 )
}
